import React, { useEffect, useMemo, useState } from 'react'
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../ui/card'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table'
import {
    AlertTriangle,
    CheckCircle2,
    Download,
    LifeBuoy,
    Loader2,
    Search,
} from 'lucide-react'
import { DateTime } from 'luxon'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import SupportEndpoints from '@/endpoints/SupportEndpoints'
import {
    SupportTicket,
    SupportTicketCategory,
    SupportTicketSeverity,
    SupportTicketStatus,
    SUPPORT_TICKET_CATEGORY_LABELS,
    SUPPORT_TICKET_SEVERITY_LABELS,
    SUPPORT_TICKET_STATUS_LABELS,
} from '@/model/SupportTicket'
import { cn } from '@/lib/utils'

const STATUS_OPEN_LIKE: SupportTicketStatus[] = [
    SupportTicketStatus.OPEN,
    SupportTicketStatus.IN_PROGRESS,
]

const STATUS_DONE_LIKE: SupportTicketStatus[] = [
    SupportTicketStatus.RESOLVED,
    SupportTicketStatus.CLOSED,
]

const isDoneStatus = (s: SupportTicketStatus) => STATUS_DONE_LIKE.includes(s)

const statusBadgeClass = (status: SupportTicketStatus): string => {
    switch (status) {
        case SupportTicketStatus.OPEN:
            return 'bg-amber-100 text-amber-900 border-amber-300'
        case SupportTicketStatus.IN_PROGRESS:
            return 'bg-blue-100 text-blue-900 border-blue-300'
        case SupportTicketStatus.RESOLVED:
            return 'bg-emerald-100 text-emerald-900 border-emerald-300'
        case SupportTicketStatus.CLOSED:
            return 'bg-slate-100 text-slate-700 border-slate-300'
    }
}

const severityBadgeClass = (severity: SupportTicketSeverity | null): string => {
    if (!severity) return 'bg-slate-100 text-slate-600 border-slate-300'
    switch (severity) {
        case SupportTicketSeverity.LOW:
            return 'bg-slate-100 text-slate-700 border-slate-300'
        case SupportTicketSeverity.MEDIUM:
            return 'bg-amber-100 text-amber-900 border-amber-300'
        case SupportTicketSeverity.HIGH:
            return 'bg-orange-100 text-orange-900 border-orange-300'
        case SupportTicketSeverity.CRITICAL:
            return 'bg-red-100 text-red-900 border-red-300'
    }
}

export const SupportTicketsPage: React.FC = () => {
    const [tickets, setTickets] = useState<SupportTicket[]>([])
    const [loading, setLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState<'open' | 'all'>('open')
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(
        null
    )
    const [updating, setUpdating] = useState(false)

    const load = async () => {
        setLoading(true)
        try {
            const list = await SupportEndpoints.listTickets()
            setTickets(list)
        } catch (error) {
            toast.error(extractErrorMessage(error, 'Failed to load tickets'))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, [])

    const filtered = useMemo(() => {
        const term = searchTerm.trim().toLowerCase()
        return tickets.filter((t) => {
            if (
                statusFilter === 'open' &&
                !STATUS_OPEN_LIKE.includes(t.status)
            ) {
                return false
            }
            if (!term) return true
            const submitterName =
                `${t.submittedBy?.firstName || ''} ${t.submittedBy?.lastName || ''}`.toLowerCase()
            return (
                t.subject.toLowerCase().includes(term) ||
                submitterName.includes(term) ||
                (t.submittedBy?.email || '').toLowerCase().includes(term) ||
                String(t.id) === term
            )
        })
    }, [tickets, statusFilter, searchTerm])

    const triage = async (
        ticket: SupportTicket,
        update: {
            status?: SupportTicketStatus
            severity?: SupportTicketSeverity
            adminNote?: string
        }
    ) => {
        setUpdating(true)
        try {
            const next = await SupportEndpoints.triageTicket(ticket.id, update)
            setTickets((prev) =>
                prev.map((t) => (t.id === next.id ? next : t))
            )
            if (selectedTicket?.id === next.id) {
                setSelectedTicket(next)
            }
            const transitioningToDone =
                update.status &&
                isDoneStatus(update.status) &&
                !isDoneStatus(ticket.status)
            if (transitioningToDone) {
                if (ticket.submittedBy?.email) {
                    toast.success(
                        `Ticket updated. Follow-up email sent to ${ticket.submittedBy.email}.`
                    )
                } else {
                    toast.success(
                        'Ticket updated. (No email on submitter — follow-up skipped.)'
                    )
                }
            } else {
                toast.success('Ticket updated.')
            }
        } catch (error) {
            toast.error(extractErrorMessage(error, 'Failed to update ticket'))
        } finally {
            setUpdating(false)
        }
    }

    return (
        <div className="container mx-auto max-w-6xl px-4 py-6">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <LifeBuoy className="h-5 w-5" />
                        Support Tickets
                    </CardTitle>
                    <CardDescription>
                        Tickets submitted by users through the "Report an issue"
                        form.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <div className="relative flex-1">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search subject, submitter, email, or ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-8"
                            />
                        </div>
                        <Select
                            value={statusFilter}
                            onValueChange={(v) =>
                                setStatusFilter(v as 'open' | 'all')
                            }
                        >
                            <SelectTrigger className="sm:w-48">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="open">
                                    Open / In Progress
                                </SelectItem>
                                <SelectItem value="all">All tickets</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <p className="rounded-md border bg-muted/30 px-3 py-6 text-center text-sm italic text-muted-foreground">
                            No tickets match the current filter.
                        </p>
                    ) : (
                        <div className="rounded-md border overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-16">#</TableHead>
                                        <TableHead>Subject</TableHead>
                                        <TableHead>Category</TableHead>
                                        <TableHead>Severity</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead>Submitter</TableHead>
                                        <TableHead className="text-right">
                                            Created
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map((t) => (
                                        <TableRow
                                            key={t.id}
                                            className="cursor-pointer"
                                            onClick={() =>
                                                setSelectedTicket(t)
                                            }
                                        >
                                            <TableCell className="font-mono text-xs text-muted-foreground">
                                                #{t.id}
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {t.subject}
                                                {t.attachments.length > 0 && (
                                                    <span className="ml-2 text-xs text-muted-foreground">
                                                        ({t.attachments.length}{' '}
                                                        attachment
                                                        {t.attachments.length !==
                                                        1
                                                            ? 's'
                                                            : ''}
                                                        )
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="text-xs">
                                                    {
                                                        SUPPORT_TICKET_CATEGORY_LABELS[
                                                            t.category
                                                        ]
                                                    }
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant="outline"
                                                    className={cn(
                                                        'text-xs',
                                                        severityBadgeClass(
                                                            t.severity
                                                        )
                                                    )}
                                                >
                                                    {t.severity
                                                        ? SUPPORT_TICKET_SEVERITY_LABELS[
                                                              t.severity
                                                          ]
                                                        : 'Untriaged'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant="outline"
                                                    className={cn(
                                                        'text-xs',
                                                        statusBadgeClass(
                                                            t.status
                                                        )
                                                    )}
                                                >
                                                    {
                                                        SUPPORT_TICKET_STATUS_LABELS[
                                                            t.status
                                                        ]
                                                    }
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {t.submittedBy
                                                    ? `${t.submittedBy.firstName} ${t.submittedBy.lastName}`
                                                    : '—'}
                                            </TableCell>
                                            <TableCell className="text-right text-sm text-muted-foreground">
                                                {DateTime.fromMillis(
                                                    Number(t.dateCreated)
                                                ).toFormat('MMM d, yyyy')}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <TicketDetailDialog
                ticket={selectedTicket}
                open={!!selectedTicket}
                onOpenChange={(open) => {
                    if (!open) setSelectedTicket(null)
                }}
                onTriage={triage}
                updating={updating}
            />
        </div>
    )
}

interface DetailDialogProps {
    ticket: SupportTicket | null
    open: boolean
    onOpenChange: (open: boolean) => void
    onTriage: (
        ticket: SupportTicket,
        update: {
            status?: SupportTicketStatus
            severity?: SupportTicketSeverity
            adminNote?: string
        }
    ) => Promise<void>
    updating: boolean
}

const TicketDetailDialog: React.FC<DetailDialogProps> = ({
    ticket,
    open,
    onOpenChange,
    onTriage,
    updating,
}) => {
    const [pendingDoneStatus, setPendingDoneStatus] =
        useState<SupportTicketStatus | null>(null)
    const [pendingNote, setPendingNote] = useState('')

    if (!ticket) return null
    const submitter = ticket.submittedBy

    const handleStatusChange = (value: string) => {
        const next = value as SupportTicketStatus
        if (next === ticket.status) return
        // Transitioning INTO a done state — pop the follow-up note flow.
        if (isDoneStatus(next) && !isDoneStatus(ticket.status)) {
            setPendingDoneStatus(next)
            setPendingNote('')
            return
        }
        onTriage(ticket, { status: next })
    }

    const confirmDoneTransition = async () => {
        if (!pendingDoneStatus) return
        await onTriage(ticket, {
            status: pendingDoneStatus,
            adminNote: pendingNote.trim() || undefined,
        })
        setPendingDoneStatus(null)
        setPendingNote('')
    }

    const cancelDoneTransition = () => {
        setPendingDoneStatus(null)
        setPendingNote('')
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <span className="font-mono text-sm text-muted-foreground">
                            #{ticket.id}
                        </span>
                        <span>{ticket.subject}</span>
                    </DialogTitle>
                    <DialogDescription>
                        Submitted{' '}
                        {DateTime.fromMillis(Number(ticket.dateCreated)).toFormat(
                            'EEE MMM d, yyyy h:mm a'
                        )}{' '}
                        by{' '}
                        {submitter
                            ? `${submitter.firstName} ${submitter.lastName}`
                            : 'unknown'}
                        {submitter?.email ? ` <${submitter.email}>` : ''}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">
                                Status
                            </Label>
                            <Select
                                value={ticket.status}
                                onValueChange={handleStatusChange}
                                disabled={updating}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(
                                        SUPPORT_TICKET_STATUS_LABELS
                                    ).map(([value, label]) => (
                                        <SelectItem key={value} value={value}>
                                            {label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">
                                Severity
                            </Label>
                            <Select
                                value={ticket.severity || ''}
                                onValueChange={(v) =>
                                    onTriage(ticket, {
                                        severity: v as SupportTicketSeverity,
                                    })
                                }
                                disabled={updating}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Untriaged" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(
                                        SUPPORT_TICKET_SEVERITY_LABELS
                                    ).map(([value, label]) => (
                                        <SelectItem key={value} value={value}>
                                            {label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">
                            Category
                        </Label>
                        <p className="text-sm">
                            {SUPPORT_TICKET_CATEGORY_LABELS[ticket.category]}
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">
                            Description
                        </Label>
                        <p className="whitespace-pre-wrap rounded-md bg-muted/30 p-3 text-sm">
                            {ticket.description}
                        </p>
                    </div>

                    {(ticket.dateResolved || ticket.resolutionNote) && (
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                                Resolution
                            </Label>
                            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
                                <div className="text-xs text-emerald-900/80 mb-2">
                                    {ticket.dateResolved && (
                                        <>
                                            Marked{' '}
                                            <span className="font-medium">
                                                {SUPPORT_TICKET_STATUS_LABELS[
                                                    ticket.status
                                                ].toLowerCase()}
                                            </span>{' '}
                                            on{' '}
                                            {DateTime.fromMillis(
                                                Number(ticket.dateResolved)
                                            ).toFormat('MMM d, yyyy h:mm a')}
                                        </>
                                    )}
                                    {ticket.resolvedBy && (
                                        <>
                                            {' '}by{' '}
                                            <span className="font-medium">
                                                {ticket.resolvedBy.firstName}{' '}
                                                {ticket.resolvedBy.lastName}
                                            </span>
                                        </>
                                    )}
                                    .
                                </div>
                                {ticket.resolutionNote ? (
                                    <p className="whitespace-pre-wrap text-emerald-950">
                                        {ticket.resolutionNote}
                                    </p>
                                ) : (
                                    <p className="text-xs italic text-emerald-900/70">
                                        No note was attached to the follow-up
                                        email.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {ticket.attachments.length > 0 && (
                        <div className="space-y-2">
                            <Label className="text-xs text-muted-foreground">
                                Attachments
                            </Label>
                            <ul className="space-y-1 rounded-md border bg-muted/30 p-2">
                                {ticket.attachments.map((a) => (
                                    <li
                                        key={a.id}
                                        className="flex items-center justify-between gap-2 px-2 py-1 text-sm"
                                    >
                                        <span className="flex-1 truncate">
                                            {a.filename}{' '}
                                            <span className="text-xs text-muted-foreground">
                                                ({a.contentType})
                                            </span>
                                        </span>
                                        <a
                                            href={SupportEndpoints.attachmentUrl(
                                                a.id
                                            )}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                                aria-label={`Download ${a.filename}`}
                                            >
                                                <Download className="h-4 w-4" />
                                            </Button>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Attachments open with the current session
                                credentials.
                            </p>
                        </div>
                    )}
                </div>
            </DialogContent>

            {/* Confirm "done" transition + optional follow-up note */}
            <Dialog
                open={!!pendingDoneStatus}
                onOpenChange={(o) => {
                    if (!o) cancelDoneTransition()
                }}
            >
                <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                        <DialogTitle>
                            Mark ticket as{' '}
                            {pendingDoneStatus
                                ? SUPPORT_TICKET_STATUS_LABELS[pendingDoneStatus]
                                : ''}
                            ?
                        </DialogTitle>
                        <DialogDescription>
                            {submitter?.email ? (
                                <>
                                    A follow-up email will be sent to{' '}
                                    <span className="font-medium">
                                        {submitter.email}
                                    </span>
                                    .
                                </>
                            ) : (
                                'No email is on file for the submitter — no follow-up email will be sent, but the ticket status will still be updated.'
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 py-2">
                        <Label
                            htmlFor="admin-note"
                            className="text-sm font-medium"
                        >
                            Note to submitter (optional)
                        </Label>
                        <Textarea
                            id="admin-note"
                            value={pendingNote}
                            onChange={(e) => setPendingNote(e.target.value)}
                            rows={5}
                            placeholder="E.g. what you changed, why it's closing, where to look, follow-up steps..."
                            disabled={updating}
                        />
                        <p className="text-xs text-muted-foreground">
                            Leave blank to send a generic notification.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={cancelDoneTransition}
                            disabled={updating}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmDoneTransition}
                            disabled={updating}
                        >
                            {updating
                                ? 'Saving...'
                                : pendingDoneStatus
                                  ? `Mark ${SUPPORT_TICKET_STATUS_LABELS[pendingDoneStatus]}`
                                  : 'Confirm'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Dialog>
    )
}
